import type { Schema } from '@dealdesk/api-schema';
import { z } from 'zod';

import { zMoney, zRequiredString } from '@/business';
import { negate } from '@/shared/lib/money';

/** Adjustment or deduction. The amount is entered positive; the direction gives the sign. */
export const adjustmentSchema = z.object({
  userId: zRequiredString(),
  direction: z.enum(['credit', 'debit', 'deduction']),
  amount: zMoney({ min: '0.01' }),
  dealId: z.string().optional(),
  reason: zRequiredString().pipe(z.string().min(3, 'form.required')),
});
export type AdjustmentValues = z.infer<typeof adjustmentSchema>;

export const adjustmentDefaults = (): AdjustmentValues => ({ userId: '', direction: 'credit', amount: '', dealId: '', reason: '' });

export function toAdjustmentRequest(v: AdjustmentValues): Schema<'StaffAdjustmentCreate'> {
  return {
    userId: v.userId,
    kind: v.direction === 'deduction' ? 'deduction' : 'adjustment',
    amount: v.direction === 'credit' ? v.amount : negate(v.amount),
    dealId: v.dealId ? v.dealId : null,
    reason: v.reason.trim(),
  };
}

/** Manual clawback against one earned entry. The amount is entered positive (it is recovered). */
export const clawbackSchema = z.object({
  userId: zRequiredString(),
  period: zRequiredString(),
  relatesToEntryId: zRequiredString(),
  amount: zMoney({ min: '0.01' }),
  reason: zRequiredString().pipe(z.string().min(3, 'form.required')),
});
export type ClawbackValues = z.infer<typeof clawbackSchema>;

export function toClawbackRequest(v: ClawbackValues): Schema<'StaffClawbackCreate'> {
  return { relatesToEntryId: v.relatesToEntryId, amount: v.amount, reason: v.reason.trim() };
}

/** Recording the payment of an approved payout run. */
export const paySchema = z.object({
  paidOn: z.string({ error: 'form.required' }).regex(/^\d{4}-\d{2}-\d{2}$/, 'form.invalidDate'),
  reference: zRequiredString().pipe(z.string().min(3, 'form.required')),
});
export type PayValues = z.infer<typeof paySchema>;
